class AjaxPagination extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.observer = null;
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    this.type = this.dataset.type || 'load-more';
    this.nextUrl = this.dataset.nextUrl;
    this.sectionId = this.dataset.section;

    if (!this.nextUrl || !this.sectionId) return;

    if (this.type === 'load-more') {
      this.button = this.querySelector('[data-ajax-pagination-button]');
      this.button?.addEventListener('click', this.onButtonClick);
    } else if (this.type === 'infinite') {
      this.setupInfiniteScroll();
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onButtonClick);
    this.observer?.disconnect();
    this.observer = null;
  }

  setupInfiniteScroll() {
    if (!('IntersectionObserver' in window)) {
      this.type = 'load-more';
      this.renderFallbackButton();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading) {
          this.loadMore();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);
  }

  renderFallbackButton() {
    if (this.querySelector('[data-ajax-pagination-button]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--primary ajax-pagination__button';
    button.setAttribute('data-ajax-pagination-button', '');
    button.innerHTML = `<span>${this.dataset.loadMoreLabel || 'Load more'}</span>`;
    this.innerHTML = '';
    this.appendChild(button);
    this.button = button;
    this.button.addEventListener('click', this.onButtonClick);
  }

  onButtonClick(event) {
    event.preventDefault();
    this.loadMore();
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.classList.toggle('is-loading', loading);
    if (this.button) this.button.disabled = loading;

    const spinner = this.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);

    const status = this.querySelector('[data-ajax-pagination-status]');
    if (status) {
      status.textContent = loading ? status.dataset.loadingText || '' : '';
    }
  }

  getFetchUrl() {
    const url = new URL(this.nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  async loadMore() {
    if (this.isLoading || !this.nextUrl) return;

    this.setLoading(true);

    try {
      const response = await fetch(this.getFetchUrl());
      if (!response.ok) throw new Error('Failed to load page');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.getElementById('product-grid');
      const currentGrid = document.getElementById('product-grid');

      if (!newGrid || !currentGrid) {
        this.setLoading(false);
        return;
      }

      this.appendResults(currentGrid, newGrid);

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(currentGrid);
      }

      const nextPagination = doc.querySelector('ajax-pagination');
      const nextUrl = nextPagination?.dataset.nextUrl;

      if (nextUrl) {
        this.nextUrl = nextUrl;
        this.dataset.nextUrl = nextUrl;
        this.setLoading(false);

        // If the sentinel is still in view after a short page, keep loading.
        if (this.type === 'infinite') {
          requestAnimationFrame(() => this.loadIfVisible());
        }
      } else {
        this.finish();
      }
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  loadIfVisible() {
    if (this.isLoading || !this.nextUrl || !this.isConnected) return;
    const rect = this.getBoundingClientRect();
    if (rect.top < window.innerHeight + 200) {
      this.loadMore();
    }
  }

  appendResults(currentGrid, newGrid) {
    if (currentGrid.tagName === 'UL') {
      currentGrid.append(...Array.from(newGrid.children));
      return;
    }

    const paginationEl =
      currentGrid.querySelector('ajax-pagination') ||
      currentGrid.querySelector('.pagination-wrapper');

    newGrid.querySelectorAll(':scope > h2.seach_inner_heading').forEach((heading) => {
      const newList = heading.nextElementSibling;
      if (!newList || newList.tagName !== 'UL') return;

      const existingHeading = Array.from(
        currentGrid.querySelectorAll(':scope > h2.seach_inner_heading')
      ).find((el) => el.textContent.trim() === heading.textContent.trim());

      if (existingHeading) {
        const existingList = existingHeading.nextElementSibling;
        if (existingList && existingList.tagName === 'UL') {
          existingList.append(...Array.from(newList.children));
          return;
        }
      }

      currentGrid.insertBefore(heading, paginationEl);
      currentGrid.insertBefore(newList, paginationEl);
    });

    if (!newGrid.querySelector(':scope > h2.seach_inner_heading')) {
      const newLists = newGrid.querySelectorAll(':scope > ul');
      const currentLists = currentGrid.querySelectorAll(':scope > ul');
      newLists.forEach((list, index) => {
        if (currentLists[index]) {
          currentLists[index].append(...Array.from(list.children));
        } else if (paginationEl) {
          currentGrid.insertBefore(list, paginationEl);
        } else {
          currentGrid.appendChild(list);
        }
      });
    }
  }

  finish() {
    this.observer?.disconnect();
    this.observer = null;
    this.nextUrl = null;
    this.removeAttribute('data-next-url');
    this.setLoading(false);
    this.remove();
  }
}

if (!customElements.get('ajax-pagination')) {
  customElements.define('ajax-pagination', AjaxPagination);
}
